import {
  MutationCache,
  QueryCache,
  type DefaultOptions as ReactQueryDefaultOptions,
  QueryClient,
  isServer
} from '@tanstack/react-query'
import type * as fetch from 'shared/api/fetch'
import { HttpStatusCodes } from 'shared/httpStatus'
import { dispatchClientError } from './components/ApiClienErrorHandler'

const STALETIME_IN_MILLI_SECONDS = Infinity // Never stale
const CACHETIME_IN_MILLI_SECONDS = 60 * 5 * 1000 // 5 minutes
const MAX_RETRY_COUNT = 2
export const RETRY_COUNT_ANALYTICS = 3

const onError = (error: unknown): void => {
  const apiError = error as fetch.ApiClientError
  // 401: session recovery (reload → login). 5xx: toast + reporting.
  if (
    apiError.status_code === HttpStatusCodes.UNAUTHORIZED ||
    apiError.status_code >= HttpStatusCodes.INTERNAL_SERVER_ERROR
  ) {
    dispatchClientError(apiError)
  }
}

export const retryFn =
  (maxRetryCount: number) =>
  (failureCount: number, error: unknown): boolean =>
    (error as fetch.ClientError).status_code >= HttpStatusCodes.INTERNAL_SERVER_ERROR && failureCount <= maxRetryCount

const defaultOptions: ReactQueryDefaultOptions = {
  queries: {
    gcTime: CACHETIME_IN_MILLI_SECONDS,
    staleTime: STALETIME_IN_MILLI_SECONDS,
    retry: retryFn(MAX_RETRY_COUNT),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    throwOnError: (error: unknown) => (error as fetch.ClientError).status_code === HttpStatusCodes.NOT_FOUND
  }
}

export const createQueryClient = (): QueryClient =>
  new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions
  })

let browserQueryClient: QueryClient | undefined

export const client = (): QueryClient => {
  if (isServer) {
    return createQueryClient()
  }

  browserQueryClient ??= createQueryClient()
  return browserQueryClient
}

export const retryDelayFn = (attemptIndex: number): number => (attemptIndex + 10) * 1000
